import React from 'react';
import { Paper, Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Link, Alert } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

dayjs.locale('it');

const TeacherDetailsCourses = ({ coursesData, teacher }) => {
  if (!coursesData || !coursesData.courses || coursesData.courses.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant='h6' sx={{ mb: 2 }}>
          Corsi e Pagamenti
        </Typography>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant='body2' color='text.secondary'>
            Nessun corso associato
          </Typography>
        </Box>
      </Paper>
    );
  }

  const getPaymentTypeLabel = (type) => {
    const labels = {
      cash: 'Contanti',
      stripe: 'Stripe',
      paypal: 'PayPal',
      bank_transfer: 'Bonifico'
    };
    return labels[type] || type;
  };

  // Calculate total compensation
  const calculateCompensation = (lessonsWithAttendance) => {
    const salaryPerLesson = teacher?.salaryPerLesson || 0;
    return (salaryPerLesson * lessonsWithAttendance).toFixed(2);
  };

  // Calculate grand total compensation
  const grandTotalCompensation = coursesData.courses.reduce((sum, courseStat) => {
    const salaryPerLesson = teacher?.salaryPerLesson || 0;
    return sum + (salaryPerLesson * courseStat.lessonsWithAttendance);
  }, 0);

  // Calculate total balance (grand total collected - grand total compensation)
  const grandTotalBalance = coursesData.grandTotal - grandTotalCompensation;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant='h6' sx={{ mb: 2 }}>
        Corsi e Pagamenti
      </Typography>
      
      <Alert severity="info" sx={{ mb: 2 }}>
        Mostra SOLO i pagamenti registrati dal docente e il totale dei compensi che deve ricevere
      </Alert>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Corso</strong></TableCell>
              <TableCell align="right"><strong>Lezioni</strong></TableCell>
              <TableCell align="right"><strong>Pagamenti</strong></TableCell>
              <TableCell align="right"><strong>Totale Incassato</strong></TableCell>
              <TableCell align="right"><strong>Totale Rimborsi</strong></TableCell>
              <TableCell align="right"><strong>Saldo</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {coursesData.courses.map((courseStat) => (
              <TableRow key={courseStat.course._id}>
                <TableCell>
                  <Box>
                    <Link
                      component={RouterLink}
                      to={`/teacher/course/${courseStat.course._id}`}
                      sx={{
                        color: '#1976d2',
                        textDecoration: 'none',
                        fontWeight: 'medium',
                        '&:hover': {
                          color: '#1565c0',
                        },
                      }}
                    >
                      <Typography variant="body1" component="span" fontWeight="medium">
                        {courseStat.course.name}
                      </Typography>
                    </Link>
                    <Typography variant="body2" color="text.secondary">
                      {courseStat.course.venue}
                    </Typography>
                    {courseStat.payments.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        {courseStat.payments.map((payment) => (
                          <>
                          <Chip
                            key={payment._id}
                            label={`€${payment.amount.toFixed(2)} - ${getPaymentTypeLabel(payment.paymentType)} - ${payment.user?.name || ''} ${payment.user?.surname || ''}`}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                          {payment.note && (
                            <Typography variant="body2" color="text.secondary">
                              {payment.note}
                            </Typography>
                          )}
                          </>
                        ))}
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {courseStat.lessonsWithAttendance}
                </TableCell>
                <TableCell align="right">
                  {courseStat.payments.length}
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="medium">
                    €{courseStat.courseTotal.toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="medium">
                    €{calculateCompensation(courseStat.lessonsWithAttendance)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {(() => {
                    const balance = courseStat.courseTotal - parseFloat(calculateCompensation(courseStat.lessonsWithAttendance));
                    return (
                      <Typography 
                        fontWeight="medium" 
                        color={balance >= 0 ? 'success.main' : 'error.main'}
                      >
                        €{balance.toFixed(2)}
                      </Typography>
                    );
                  })()}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={3}>
                <Typography variant="h6" fontWeight="bold">
                  Totale Generale
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="h6" fontWeight="bold">
                  €{coursesData.grandTotal.toFixed(2)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="h6" fontWeight="bold">
                  €{grandTotalCompensation.toFixed(2)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography 
                  variant="h6" 
                  fontWeight="bold"
                  color={grandTotalBalance >= 0 ? 'success.main' : 'error.main'}
                >
                  €{grandTotalBalance.toFixed(2)}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default TeacherDetailsCourses;
